import { createSlice } from "@reduxjs/toolkit";
import { ORDER_RECEIVE_CART_PRODUCT_TYPE, MARGIN_TYPE, dateFormat } from "../../../Common/constants";
import { calculateCost, calculateMargin, calculatePrice,  formatCurrency, formatCurrencyCustom, getCurrentUtcDateTime, getTimeByTimeZoneOnMoment } from "../../../Common/utils";

const initialOrderReceiveState = {
  edit: false,
  entities: [],
  orderNo: null,
  orderId: null,
  supplier: null,
  products: [],
  initialProducts: [],
  variantReceivedProducts: [],
  newProducts: [],
  statusChanged: false,
  subTotal: 0,
  listLoading: false,
  actionsLoading: false,
  totalCount: 0,
  productCartStatus: null,
  completed: false,
  warehouseData: null,
  warehouseOption: null,
  deliveryDate: null,
  receivingDate: null,
  supplierInvoiceDate: null,
  supplierInvoiceNo: null,
  currentOrder: {},
  orderNote: null,
  variantReceivedSubTotal: 0,
  orderPrice: 0,
  entity: {},
  purchaseReceivesEntities: [],
  selectedOrder: null,
  selectedReceive: null,
  currentReceive: null,
  scanItem: "",
  productAvgCosts: {},
  // Purchase order the current productAvgCosts belong to. Responses for any other order are discarded.
  productAvgCostsOrderId: null,
  message: {
    error: null,
    success: null,
    cartError: null,
    cartSuccess: null,
  },
};
export const callTypes = {
  list: "list",
  action: "action",
};

export const purchaseOrderReceiveSlice = createSlice({
  name: "purchaseOrdersReceives",
  initialState: initialOrderReceiveState,
  reducers: {
    catchError: (state, action) => {
      let error =
        action.payload.error.response && action.payload.error.response.data ? action.payload.error.response.data.errors : action.payload.error.message;
      if (error.constructor.name === "Object") {
        let temp = [];
        for (let [k, v] of Object.entries(error)) {
          temp.push(`${k}->${v}`);
        }
        error = temp;
      }
      state.message.error = error;
      if (action.payload.callType === callTypes.list) {
        state.listLoading = false;
      } else {
        state.actionsLoading = false;
      }
    },
    startCall: (state, action) => {
      state.message.success = null;
      state.error = null;
      if (action.payload.callType === callTypes.list) {
        state.listLoading = true;
      } else {
        state.actionsLoading = true;
      }
    },

    applyCustomValues: (state, action) => {
      const { additional_discounts: customDiscounts, additional_charges: customCharges } = action.payload;
      let totalCost = 0;
      let totalCharges = 0;
      let totalDiscount = 0;

      state.products.forEach((product) => {
        totalCost += product.cost * product.orderQty;
      });

      customCharges.forEach((charge) => (totalCharges += charge.value));
      customDiscounts.forEach((discount) => (totalDiscount += discount.value));

      state.products = state.products.map((product) => {
        const product_total = +formatCurrencyCustom(product.cost * +(product.orderQty - (product.rcv_qty - product.rtn_qty)));
        const additional_charges_share = (totalCharges / totalCost) * product_total;
        const per_unit_charges = additional_charges_share / product.quantity;
        const additional_discount_share = (totalDiscount / totalCost) * product_total;
        const per_unit_discount = additional_discount_share / product.orderQty;
        const newCost = +formatCurrencyCustom(+product.cost + (per_unit_charges - per_unit_discount));
        return { ...product, cost: newCost, margin: product.price - newCost };
      });
    },
    applyInitialValues: (state, action) => {
      state.products = state.initialProducts;
    },

    // findProducts
    productsFetched: (state, action) => {
      const { totalCount, entities, searchKey, fromScanner } = action.payload;
      if (fromScanner && (entities[0].upc == searchKey || entities[0].sku == searchKey)) {
        state.newProducts.push({ ...entities[0], quantity: 1 });
      } else {
        state.entities = entities;
        state.totalCount = totalCount;
        state.products.map((product) => {
          state.entities = state.entities.filter((entity) => entity.id != product.id);
        });
        state.newProducts.map((product) => {
          state.entities = state.entities.filter((entity) => entity.id != product.id);
        });
        state.variantReceivedProducts.map((product) => {
          state.entities = state.entities.filter((entity) => entity.id != product.id);
        });
      }
      state.listLoading = false;
      state.error = null;
    },

    purchaseReceivesFetched: (state, action) => {
      state.listLoading = false;
      state.purchaseReceivesEntities = action.payload.entities;
      state.entities = action.payload.entities;
      state.totalCount = action.payload.totalCount;
    },
    //List For PR Searching Field
    purchaseReceivesList: (state, action) => {

      state.listLoading = false;
      state.entities = action.payload.entities;
    },
    //remove product added to an order
    removeSelectedProduct: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { id } = action.payload;
      state.entities = state.entities.filter((product) => product.id != id);
    },

    // create Purchase Receive

    purchaseReceiveCreated: (state, action) => {
      const { message, entity } = action.payload;
      state.currentReceive = {
        ...entity,
        purchase_receive_details: entity.purchase_receive_details.map((item) => {
          return {
            ...item,
            supply_price: item.supply_price,
            order_qty: item.order_qty,
            qty: item.qty,
            total_supplier_price: item.total_supplier_price,
            qty_in_piece: item.ordered_qty,
          };
        }),
      };
      state.message.success = message;
      state.entity = entity;
      state.actionsLoading = false;
      state.error = null;
      state.completed = true;
    },

    purchaseReceiveUpdated: (state, action) => {
      const { message, entity } = action.payload;
      state.currentReceive = {
        ...entity,
        purchase_receive_details: entity.purchase_receive_details.map((item) => {
          return {
            ...item,
            supply_price: item.supply_price,
            order_qty: item.order_qty,
            qty: item.qty,
            total_supplier_price: item.total_supplier_price,
            qty_in_piece: item.ordered_qty,
          };
        }),
      };
      state.message.success = message;
      state.entity = entity;
      state.actionsLoading = false;
      state.error = null;
      state.completed = true;
    },

    productPricesUpdated: (state, action) => {
      const { message } = action.payload;
      state.message.success = message;
      state.actionsLoading = false;
      state.error = null;
      state.completed = true;
    },

    // createOrder
    // purchaseReceiveCreated: (state, action) => {
    //   const { message, entity } = action.payload;
    //   ///Creating Invoice From Local State Variables
    //   let InvoicePayload = {};
    //   InvoicePayload.id = entity.id;
    //   InvoicePayload.remaining_amount = entity.remaining_amount;
    //   InvoicePayload.created_at = entity.created_at;
    //   InvoicePayload.purchase_receive_no = entity.no;
    //   InvoicePayload.purchase_order_no = entity.purchase_order.no;
    //   InvoicePayload.receiving_date = entity.receiving_date;
    //   InvoicePayload.supplier_invoice_date = entity.supplier_invoice_date;
    //   InvoicePayload.supplier_invoice = entity.supplier_invoice;
    //   InvoicePayload.status = entity.status;
    //   InvoicePayload.ord_grand_total = state.orderPrice;
    //   let rcv_total = 0;
    //   entity.purchase_receive_details.map((prd) => {
    //     rcv_total += prd.total_price;
    //   });

    //   InvoicePayload.rcv_grand_total = rcv_total;
    //   InvoicePayload.purchase_order = entity.purchase_order;
    //   InvoicePayload.purchase_receive_details = state.products.map((product) => {
    //     let pord = {};
    //     pord.ord_qty = product.orderQty;
    //     pord.rcv_qty = product.rcv_qty + product.quantity;
    //     pord.ord_supply_price = product.order_supply_price;
    //     pord.rcv_supply_price = parseFloat(product.cost);
    //     pord.ord_total_price = parseFloat(pord.ord_qty) * parseFloat(pord.ord_supply_price);
    //     pord.rcv_total_price = parseFloat(pord.rcv_qty) * parseFloat(pord.rcv_supply_price);
    //     pord.product = product;
    //     // pord.ord_supply_price = product.price;
    //     return pord;
    //   });
    //   state.newProducts.map((product) => {
    //     let pord = {};
    //     pord.ord_qty = 0;
    //     pord.rcv_qty = product.rcv_qty;
    //     pord.ord_supply_price = product.cost;
    //     pord.rcv_supply_price = parseFloat(product.cost);
    //     pord.ord_total_price = parseFloat(pord.ord_qty) * parseFloat(pord.ord_supply_price);
    //     pord.rcv_total_price = parseFloat(pord.rcv_qty) * parseFloat(pord.rcv_supply_price);
    //     pord.product = product;
    //     InvoicePayload.purchase_receive_details.push(pord);
    //   });
    //   state.currentReceive = InvoicePayload;
    //   state.message.success = message;
    //   state.actionsLoading = false;
    //   state.completed = true;
    // },
    addProduct: (state, action) => {
      state.error = null;
      state.newProducts.push(action.payload.product);
      state.message.success = "Product added to cart";
      state.added = true;
    },
    updateQuantityByOne: (state, action) => {
      const product = action.payload[0];
      state.products = state.products.map((entity) => {
        if (product.id === entity.id) {
          entity.quantity = Number(entity.quantity) + 1;
          entity.subTotal = entity.quantity * entity.cost;
        }
        return entity;
      });
      state.newProducts = state.newProducts.map((entity) => {
        if (product.id === entity.id) {
          entity.quantity = Number(entity.quantity) + 1;
          entity.subTotal = entity.quantity * entity.cost;
        }
        return entity;
      });
    },
    scanProduct: (state, action) => {
      state.actionsLoading = false;
      state.message.error = null;
      const { entity, queryParams } = action.payload;
      if (entity?.sku === queryParams?.searchKey?.trim() || entity?.upc === queryParams?.searchKey?.trim()) {
        state.scanItem = entity;
      } else {
        state.scanItem = "notFound";
      }
    },
    removeScanItem: (state) => {
      state.scanItem = null;
    },
    changeReceiveStatus: (state, action) => {
      state.error = null;
      state.actionsLoading = false;
      const { purchaseReceive } = action.payload;
      state.statusChanged = true;
      state.message.success = "Status changed successfully";
      state.purchaseReceivesEntities = state.purchaseReceivesEntities.map((entity) => {
        if (purchaseReceive.id === entity.id) {
          entity.status = purchaseReceive.status;
        }
        return entity;
      });
    },
    // change the status
    changeResponseMessage: (state, action) => {
      state.message.success = null;
    },
    rejectReceipt: (state, action) => {
      state.error = null;
      state.actionsLoading = false;
      const { entity } = action.payload;
      state.message.success = "Purchase rejected successfully";
      state.purchaseReceivesEntities = state.purchaseReceivesEntities.map((e) => {
        if (e.id === entity.id) {
          e = entity;
          e.purchase_receive_no = entity.no;
          e.purchase_order_no = entity.purchase_order.no;
          e.total_amount = entity.total_amount;
          e.remaining_amount = entity.remaining_amount;
          var ord_grand_total = 0;
          e.purchase_receive_details.map((stateObj) => {
            entity.purchase_receive_details.map((respObj) => {
              if (stateObj.id == respObj.id) {
                stateObj.rcv_supply_price = respObj.supply_price;
                stateObj.rcv_qty = respObj.qty;
                stateObj.rcv_total_price = respObj.total_price;
              }
            });
            entity.purchase_order.purchase_order_details.map((respObj) => {
              if (stateObj.product.id == respObj.product.id) {
                stateObj.ord_supply_price = respObj.supply_price;
                stateObj.ord_qty = respObj.qty;
                stateObj.ord_total_price = respObj.total_price;
                ord_grand_total += stateObj.ord_total_price;
              }
            });
          });
          e.ord_grand_total = ord_grand_total;
          e.total_amount = ord_grand_total;
        }
        return e;
      });
      state.statusChanged = true;
    },

    //update product quantity
    updateQuantity: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { product, quantity, type } = action.payload;
      if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.NEW) {
        state.newProducts = state.newProducts.map((entity) => {
          if (product.id === entity.id) {
            entity.quantity = quantity;
            entity.rcv_qty = quantity;
            entity.subTotal = entity.quantity * entity.cost;
          }
          return entity;
        });
      } else if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.OLD) {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            let totalQty = quantity + (product.rcv_qty - product.rtn_qty);
            if (product.orderQty >= totalQty) {
              entity.moreReceived = undefined;
              entity.lessReceived = Number(product.orderQty - totalQty);
              entity.lessReceivedPrice = entity.lessReceived * entity.cost;
              entity.newSubTotal = entity.lessReceived * entity.cost;
              state.variantReceivedProducts = state.variantReceivedProducts.filter((item) => product.id != item.id);
              state.variantReceivedProducts.push(entity);
            } else if (product.orderQty < totalQty) {
              entity.lessReceived = undefined;
              entity.lessReceivedPrice = 0;
              entity.moreReceived = Number(totalQty - product.orderQty);
              entity.newSubTotal = entity.moreReceived * entity.cost;
              state.variantReceivedProducts = state.variantReceivedProducts.filter((item) => product.id != item.id);
              state.variantReceivedProducts.push(entity);
            } else {
              entity.lessReceived = undefined;
              entity.moreReceived = undefined;
              entity.lessReceivedPrice = entity.subTotal;
              entity.newSubTotal = entity.subTotal;
              state.variantReceivedProducts = state.variantReceivedProducts.filter((item) => product.id != item.id);
            }
            entity.quantity = quantity;
            // entity.rcv_qty = quantity;
          }
          return entity;
        });
      } else if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.VARIANT) {
        const qty = product.oldQuantity - quantity;
        if (quantity >= product.oldQuantity) {
          state.variantReceivedProducts = state.variantReceivedProducts.map((entity) => {
            if (product.id === entity.id) {
              entity.quantity = product.oldQuantity;
              entity.subTotal = entity.quantity * entity.cost;
            }
            return entity;
          });
          if (state.products.some((item) => item.id == product.id)) {
            state.products = state.products.filter((item) => product.id != item.id);
          }
        } else {
          state.variantReceivedProducts = state.variantReceivedProducts.map((entity) => {
            if (product.id === entity.id) {
              entity.quantity = quantity;
              entity.subTotal = entity.quantity * entity.cost;
            }
            return entity;
          });
          if (state.products.some((item) => item.id == product.id)) {
            state.products = state.products.map((entity) => {
              if (product.id === entity.id) {
                entity.quantity = qty;
                entity.subTotal = entity.quantity * entity.cost;
              }
              return entity;
            });
          }
        }
      }
    },

    obj: {
      price: 20,
      margin: 20,
      margin_type: "%",
    },
    //change margin
    changeMargin: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { product, type, changeType } = action.payload;
      const { cost, margin, price, margin_type } = action.payload.margin;

      let MarginType = !margin_type ? product.margin_type : margin_type;
      if (margin_type) {
        MarginType = margin_type;
      }
      // if (changeType == "margin" && margin == "") {
      //   state.products = state.products.map((entity) => {
      //     if (product.id === entity.id) {
      //       entity.price = cost;
      //       entity.cost = cost;
      //       entity.margin = margin;

      //       entity.subTotal = entity.quantity * cost;
      //     }
      //     return entity;
      //   });
      //   return;
      // }
      // if (changeType == "margin") {
      //   state.products = state.products.map((entity) => {
      //     if (product.id === entity.id) {
      //       entity.price = price;
      //       entity.cost = cost;
      //       entity.margin = margin;
      //       entity.subTotal = entity.quantity * cost;
      //     }
      //     return entity;
      //   });
      //   return;
      // }

      if (changeType == "margin") {
        if (product.cost > 0) {
          state.products = state.products.map((entity) => {
            if (product.id === entity.id) {
              entity.price = price;
              entity.cost = cost;
              entity.margin_type = margin_type;

              entity.margin = formatCurrencyCustom(calculateMargin(cost, entity.price, MarginType));
              if (margin == 0) {
                entity.cost = cost;
              }
              entity.subTotal = entity.quantity * cost;
            }
            return entity;
          });
        } else if (product.margin != "") {
          state.products = state.products.map((entity) => {
            if (product.id === entity.id) {
              entity.price = price;
              entity.margin_type = margin_type;
              entity.margin = margin;
              entity.cost = formatCurrencyCustom(calculateCost(price, margin, MarginType));
              if (margin == 0) {
                entity.cost = cost;
              }
              entity.subTotal = entity.quantity * cost;
            }
            return entity;
          });
        } else {
          state.products = state.products.map((entity) => {
            if (product.id === entity.id) {
              entity.price = price;
              entity.cost = cost;
              entity.margin = margin;
              entity.subTotal = entity.quantity * cost;
            }
            return entity;
          });
        }
      } else {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            entity.price = price;
            entity.cost = cost;
            entity.margin = margin;
            entity.margin = formatCurrencyCustom(calculateMargin(cost, price, MarginType));
            entity.subTotal = entity.quantity * cost;
          }
          return entity;
        });
      }
    },

    changeCost: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { product, type, changeType, margin_type } = action.payload;
      const { cost, margin, price } = action.payload.margin;
      // for making changePrice(margin_type) optional in arguments
      let MarginType = product.margin_type;
      if (margin_type) {
        MarginType = margin_type;
      }

      if (changeType == "cost" && !cost) {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            entity.cost = cost;
            entity.price = cost;
            entity.margin = margin;
          }
          return entity;
        });
      }

      if (changeType == "cost" && !cost && !price) {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            entity.cost = cost;
          }
          return entity;
        });
      }
      if (changeType != "cost") {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            entity.margin = margin;
            entity.price = price;
            entity.cost = cost;
            entity.cost = formatCurrencyCustom(calculateCost(entity.price, margin, MarginType));
            if (entity.cost == 0) {
              entity.price = 0;
            }
            entity.subTotal = entity.quantity * entity.cost;
          }
          return entity;
        });
      }
    },

    //change product price
    changePrice: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { product, type, changeType, margin_type } = action.payload;
      const { cost, margin, price } = action.payload.margin;

      // for making changePrice(margin_type) optional in arguments
      let MarginType = product.margin_type;
      if (margin_type) {
        MarginType = margin_type;
      }
      //

      if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.NEW) {
        state.newProducts = state.newProducts.map((entity) => {
          if (product.id === entity.id) {
            entity.cost = cost;
            entity.margin = margin;
            entity.margin_type = MarginType;
            entity.price = formatCurrencyCustom(calculatePrice(entity.cost, margin, type));
            entity.subTotal = entity.quantity * entity.cost;
          }
          return entity;
        });
      } else if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.OLD) {
        state.products = state.products.map((entity) => {
          if (product.id === entity.id) {
            entity.cost = cost;
            entity.margin = margin;
            entity.margin_type = !!MarginType ? MarginType : entity.margin_type;
            entity.price = entity.margin ? formatCurrencyCustom(calculatePrice(entity.cost, entity.margin, entity.margin_type)) : entity.price;
            entity.subTotal = entity.quantity * entity.cost;
          }
          return entity;
        });
        state.variantReceivedProducts = state.variantReceivedProducts.map((entity) => {
          if (entity.id == product.id) {
            entity.price = price;
            entity.cost = cost;
            entity.margin = margin;
            entity.margin_type = MarginType;
            entity.price = formatCurrencyCustom(calculatePrice(entity.cost, margin, type));
            entity.subTotal = entity.quantity * entity.cost;
          }
          return entity;
        });
      }
    },

    addPayment: (state, action) => {
      // state.message.success = null;
      const { entity, success } = action.payload;
      let purchaseReceivesEntities = JSON.parse(JSON.stringify(state.purchaseReceivesEntities));

      state.actionsLoading = false;
      state.purchaseReceivesEntities = purchaseReceivesEntities.map((item) => {
        if (entity && item.id === entity.id) {
          item.payments = entity.payments;
          item.remaining_amount = entity.remaining_amount;
          item.status = entity.status;
        }
        return item;
      });
      state.message.success = success;
    },

    setWarehouse: (state, action) => {
      state.warehouseOption = action.payload.warehouseOption;
      state.warehouseData = action.payload.warehouseData;
    },
    setDeliveryDate: (state, action) => {
      state.deliveryDate = action.payload.deliveryDate;
    },
    setReceivingDate: (state, action) => {
      state.receivingDate = action.payload.receivingDate;
    },
    setSupplierInvoiceDate: (state, action) => {
      state.supplierInvoiceDate = action.payload.supplierInvoiceDate;
    },
    setSupplierInvoiceNo: (state, action) => {
      state.supplierInvoiceNo = action.payload.supplierInvoiceNo;
    },
    resetStates: (state, action) => {
      state.entities = [];
      state.productAvgCosts = {};
      state.productAvgCostsOrderId = null;
      state.selectedOrder = null;
      state.selectedReceive = null;
      state.edit = false;
      state.supplier = null;
      state.products = [];
      state.completed = false;
      state.newProducts = [];
      state.variantReceivedProducts = [];
      state.deliveryDate = null;
      state.receivingDate = null;
      state.supplierInvoiceDate = null;
      state.error = null;
      state.message.error = null;
      state.orderPrice = null;
      state.subTotal = null;
      state.variantReceivedSubTotal = null;
      state.message = {
        error: null,
        success: null,
        cartError: null,
        cartSuccess: null,
      };
    },
    resetPurchaseReceiveState: (state, action) => {
      state.entities = [];
      state.orderNo = null;
      state.orderId = null;
      state.supplier = null;
      state.products = [];
      state.variantReceivedProducts = [];
      state.newProducts = [];
      state.statusChanged = false;
      state.subTotal = 0;
      state.listLoading = false;
      state.actionsLoading = false;
      state.totalCount = 0;
      state.productCartStatus = null;
      state.completed = false;
      state.warehouseData = null;
      state.warehouseOption = null;
      state.deliveryDate = null;
      state.receivingDate = null;
      state.supplierInvoiceDate = null;
      state.currentOrder = {};
      state.orderNote = null;
      state.variantReceivedSubTotal = 0;
      state.orderPrice = 0;
      state.entity = {};
      state.purchaseReceivesEntities = [];
      state.selectedOrder = null;
      state.currentReceive = null;
      state.scanItem = "";
      state.productAvgCosts = {};
      state.productAvgCostsOrderId = null;
      state.message = {
        error: null,
        success: null,
        cartError: null,
        cartSuccess: null,
      };
    },
    rePurchaseReceiveState: (state, action) => {
      state.completed = false;
    },
    setSelectedReceive: (state, action) => {
      const { orderReceive } = action.payload;
      state.selectedReceive = orderReceive;
      state.edit = true;
    },
    setEditReceive: (state, action) => {
      state.edit = action.payload;
    },
    clearProductAvgCosts: (state, action) => {
      const orderId = action.payload;
      state.productAvgCosts = {};
      state.productAvgCostsOrderId = orderId == null ? null : String(orderId);
    },
    setProductAvgCosts: (state, action) => {
      const { orderId, prices } = action.payload || {};
      // Ignore a late response for an order the user has already navigated away from.
      if (orderId == null || String(orderId) !== state.productAvgCostsOrderId) {
        return;
      }
      state.productAvgCosts = prices || {};
    },
    setSelectedOrder: (state, action) => {
      const { order, timezone } = action.payload;
      state.selectedOrder = order;
      let warehouseOption = {};
      let dateTime = getCurrentUtcDateTime(timezone);
      const todayDate = dateTime?.format(dateFormat);
      let orderPrice = 0;
      let isReceived = false;
      order.purchase_order_details.forEach((detail) => {
        if (detail.received_qty > 0) {
          isReceived = true;
          return;
        }
      });
      let products = order.purchase_order_details.map((d) => {
        let product = JSON.parse(JSON.stringify(d.product));
        const oldCost = isReceived ? d.supply_price_after_tax : (d?.supply_price_after_tax || d?.product?.unit_prices[d?.unit]?.base_cost);
        const _diff = d?.product?.unit_prices[d?.unit].price - (oldCost || d?.supply_price_after_tax);
        const margin = d?.product?.unit_prices[d?.unit]?.margin_type == MARGIN_TYPE.FIXED ? _diff : (_diff / (oldCost || d?.supply_price_after_tax)) * 100;
        product.purchase_order_detail_v2_id = d.id;
        product.totalPrice = d.total_price;
        product.unit = d.unit;
        product.unit_name = d.unit_name;
        product.tax = d.tax;
        product.tax_values = d.tax_values;
        product.tax_attributes = d.tax_attributes;
        product.lowest_unit_definition = d?.product?.unit_prices[d?.unit]?.lowest_unit_definition;
        product.orderQty = d.ordered_qty;
        product.quantity = d.ordered_qty - (d.received_qty - d.returned_qty);
        product.lessReceived = 0;
        product.newPrice = oldCost;
        product.cost = oldCost || d?.supply_price;
        product.oldCost = oldCost;
        product.margin = formatCurrency(margin);
        product.margin_type = d?.product?.unit_prices[d?.unit]?.margin_type;
        product.price = formatCurrencyCustom(d?.product?.unit_prices[d?.unit].price);
        product.rcv_qty = d.received_qty;
        product.rtn_qty = d.returned_qty;
        product.supply_price = d.supply_price;  // By default NCP toggle off, that's why just adding the supply_price, if need tax just right d.supply_price_after_tax
        product.order_supply_price = d.supply_price;
        orderPrice += d.total_price;
        product.position = d.position;
        product.otherPrices = d.otherPrices;

        return product;
      });
      state.orderPrice = orderPrice;
      state.supplier = order.supplier;
      state.deliveryDate = getTimeByTimeZoneOnMoment(timezone, order.delivery_date);
      state.orderNote = order.note;
      state.warehouseData = order.channel;
      warehouseOption.value = order.channel.id;
      warehouseOption.key = order.channel.id;
      warehouseOption.label = order.channel.name;
      state.warehouseOption = warehouseOption;
      state.products = products;
      state.initialProducts = products;
      state.orderNo = order.no || null;
      state.orderId = order.id;
      state.receivingDate = getTimeByTimeZoneOnMoment(timezone, order.delivery_date);
      state.supplierInvoiceDate = order?.currentDate || todayDate;
    },
    removeSelectedOrder: (state, action) => {
      state.selectedOrder = null;
    },
    statusChanged: (state, action) => {
      state.message.success = null;
      state.statusChanged = action.payload.statusChanged;
    },
    setCompleted: (state, action) => {
      state.completed = false;
    },

    //remove product from cart
    removeProduct: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { product, type } = action.payload;
      if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.NEW) {
        state.newProducts = state.newProducts.filter((item) => item.id != product.id);
        state.entities.push(product);
        state.message.cartSuccess = "Product removed from cart";
      } else if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.OLD) {
        if (state.variantReceivedProducts.some((item) => item.id == product.id)) {
          state.variantReceivedProducts = state.variantReceivedProducts.map((entity) => {
            if (product.id === entity.id) {
              entity.quantity = 0;
              entity.subTotal = entity.quantity * entity.cost;
              entity.newSubTotal = entity.cost;
              entity.lessReceived = entity.orderQty;
              entity.lessReceivedPrice = entity.cost;
              entity.moreReceived = undefined;
            }
            return entity;
          });
        } else {
          let parsedProduct = JSON.parse(JSON.stringify(product));
          parsedProduct.quantity = 0;
          parsedProduct.lessReceived = product.orderQty;
          parsedProduct.moreReceived = undefined;
          parsedProduct.newSubTotal = parsedProduct.totalPrice;
          parsedProduct.lessReceivedPrice = parsedProduct.totalPrice;
          state.variantReceivedProducts.push(parsedProduct);
        }
        state.message.cartSuccess = "Product added to returns";
        state.products = state.products.filter((item) => item.id != product.id);
      } else if (type == ORDER_RECEIVE_CART_PRODUCT_TYPE.VARIANT) {
        if (state.products.some((item) => item.id == product.id)) {
          state.products = state.products.map((entity) => {
            if (product.id === entity.id) {
              entity.quantity = entity.orderQty;
              entity.subTotal = entity.cost * entity.quantity;
              entity.lessReceivedPrice = 0;
            }
            return entity;
          });
        } else {
          let parsedProduct = JSON.parse(JSON.stringify(product));
          parsedProduct.quantity = product.orderQty;
          parsedProduct.lessReceivedPrice = 0;
          state.products.push(parsedProduct);
        }
        state.variantReceivedProducts = state.variantReceivedProducts.filter((item) => item.id != product.id);
        state.message.cartSuccess = "Product added to cart";
      }
      state.productCartStatus = null;
    },

    // updateOrder
    updateSubTotal: (state, action) => {
      state.error = null;
      const { subTotal, total, variantReceivedSubTotal } = action.payload;
      state.subTotal = subTotal;
      state.variantReceivedSubTotal = variantReceivedSubTotal;
    },
    //update message
    updateMessage: (state, action) => {
      const { type, message } = action.payload;
      switch (type) {
        case "error":
          state.message.error = message;
          break;
        case "success":
          state.message.success = message;
          break;
        case "cartSuccess":
          state.message.cartSuccess = message;
          break;
        case "cartError":
          state.message.cartError = message;
          break;
      }
    },
  },
});